import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { SpinnerComponent } from '@mzic/mzic-components';
import { ConnectionStatus } from '../../interfaces/collection.interface';

@Component({
  selector: 'lib-resume-connection-dialog',
  imports: [CommonModule, A11yModule, TranslocoModule, SpinnerComponent],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './resume-connection-dialog.component.html',
  styleUrl: './resume-connection-dialog.component.scss',
})
export class ResumeConnectionDialogComponent implements AfterViewInit {
  @Input() entityName = '';
  @Input() entityIconUrl: string | null = null;
  @Input() connectionStatus: ConnectionStatus = 'PENDING';
  @Input() authInteractionTitle: string | null = null;
  @Input() authInteractionInstructions: string | null = null;
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() loading = false;

  @Output() resumeRequested = new EventEmitter<void>();
  @Output() cancelRequested = new EventEmitter<void>();

  readonly titleId = 'resume-connection-title';
  readonly descriptionId = 'resume-connection-description';

  @ViewChild('resumeButton', { read: ElementRef })
  private resumeButton?: ElementRef<HTMLButtonElement>;

  ngAfterViewInit(): void {
    this.resumeButton?.nativeElement.focus();
  }

  get entityInitial(): string {
    return this.entityName?.trim()?.[0]?.toUpperCase() || '?';
  }

  get subtitleText(): string | null {
    if (
      this.connectionStatus === 'AWAITING_AUTH_INTERACTION' &&
      this.authInteractionInstructions
    ) {
      return this.authInteractionInstructions;
    }

    if (
      this.connectionStatus === 'AWAITING_AUTH_INTERACTION' &&
      this.authInteractionTitle
    ) {
      return this.authInteractionTitle;
    }

    return null;
  }

  get subtitleKey(): string {
    const suffix = this.integrationEnabled ? '_integration' : '';

    switch (this.connectionStatus) {
      case 'AUTHENTICATING':
      case 'ACTIVE':
      case 'PENDING':
        return `lang.resume_dialog_subtitle_active${suffix}`;
      case 'AWAITING_AUTH_INTERACTION':
        return `lang.resume_dialog_subtitle_auth_interaction${suffix}`;
      case 'NEEDS_CREDENTIALS':
      case 'EXPIRED':
      case 'REVOKED':
        return `lang.resume_dialog_subtitle_credentials${suffix}`;
      default:
        return `lang.resume_dialog_subtitle_generic${suffix}`;
    }
  }

  onResume(): void {
    if (this.loading) return;
    this.resumeRequested.emit();
  }

  onCancel(): void {
    if (this.loading) return;
    this.cancelRequested.emit();
  }
}
